import { useState, useEffect } from "react";
import { defaultOrderByFn } from "react-table";
import { remove } from "lodash";

/**
 * Returns a function that can be used to update the react-table state for pinned rows
 */
export const usePinnedRows = (rows, initialPinnedRows, getInitialPinId) => {
  const [rowState, setRowState] = useState({});

  const getInitialRowState = row => {
    const pinId = getInitialPinId(row, initialPinnedRows);
    return {
      isPinned: !!pinId,
      pinId: pinId
    };
  };

  useEffect(() => {
    if (rows) {
      const newRowState = rows.reduce(
        (state, row) => ({ ...state, [row.id]: getInitialRowState(row) }),
        {}
      );
      setRowState(newRowState);
    }
  }, [rows, initialPinnedRows]);

  const updateRowState = (row, newState) => {
    setRowState({
      ...rowState,
      [row.id]: { ...rowState[row.id], ...newState }
    });
  };

  const pinRow = (row, createPinCallback, deletePinCallback) => {
    const pinId = rowState[row.id].pinId;
    updateRowState(row, { isUpdating: true, isPinned: !pinId });
    if (!!pinId) {
      deletePinCallback(pinId).then(() =>
        updateRowState(row, {
          isPinned: false,
          pinId: null,
          isUpdating: false
        })
      );
    } else {
      createPinCallback().then(newPin => {
        if (newPin) {
          updateRowState(row, {
            isPinned: true,
            pinId: newPin.id,
            isUpdating: false
          });
        }
      });
    }
  };

  const sortRows = (rows, sortFns, desc) => {
    let unpinnedRows = [...rows];
    let pinnedRows = remove(unpinnedRows, row => isRowPinned(row.id));
    pinnedRows = defaultOrderByFn(pinnedRows, sortFns, desc);
    unpinnedRows = defaultOrderByFn(unpinnedRows, sortFns, desc);
    return [...pinnedRows, ...unpinnedRows];
  };

  const isRowPinned = rowId => rowState[rowId] && rowState[rowId].isPinned;
  const isPinUpdating = rowId => rowState[rowId] && rowState[rowId].isUpdating;

  return { pinRow, sortRows, isRowPinned, isPinUpdating };
};
